<template>
    <div class="relative flex items-center" data-v-step="announcement">
        <icon
            class="absolute right-0 top-0 z-10"
            :class="[currentStatusColor, { 'availability-status': !mdLayout }]"
            name="bold/sign-badge-circle"
            scale="0.5"
        />

        <el-dropdown
            class="overflow-hidden"
            trigger="click"
            placement="bottom-start"
            @command="onDropdownCommand"
        >
            <activix-tooltip placement="bottom" :content="userTooltip">
                <div data-testid="main-header-user-profile-trigger" class="flex items-center group">
                    <activix-avatar
                        class="rounded-full shadow-inner text-gray-650"
                        :class="mdLayout ? 'text-xl' : 'text-2xl'"
                        :size="mdLayout ? 28 : 35"
                        :username="authUser.fullName"
                        :src="authUser.hasProfilePicture() ? authUser.profile_picture : ''"
                    />
                    <icon
                        class="text-grey-600 ml-3 text-xs | group-hover:text-grey-700"
                        :name="$icons.expand"
                        v-if="!mdLayout"
                    />
                </div>
            </activix-tooltip>
            <template #dropdown>
                <el-dropdown-menu>
                    <el-dropdown-item command="openProfile" v-if="!mdLayout">
                        <div class="flex items-center">
                            <icon
                                class="mr-2 text-grey-500"
                                name="regular/single-neutral-actions"
                            />
                            {{ $t('navs.userDropdown.myProfile') }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item
                        command="openAccount"
                        v-if="!mdLayout && authUser.hasAccessTo('accounts.update')"
                    >
                        <div class="flex items-center">
                            <icon class="mr-2 text-grey-500" name="regular/shop" />
                            {{ $t('navs.userDropdown.myAccount') }}
                        </div>
                    </el-dropdown-item>

                    <el-dropdown-item
                        command="openGroups"
                        divided
                        v-if="!mdLayout && authUser.hasAccessTo('groups.index')"
                    >
                        <div class="flex items-center">
                            <icon class="mr-2 text-grey-500" name="regular/color-palette" />
                            {{ $t('navs.userDropdown.groups') }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item
                        command="openAccounts"
                        v-if="!mdLayout && authUser.hasAccessTo('accounts.index')"
                    >
                        <div class="flex items-center">
                            <icon class="mr-2 text-grey-500" name="regular/shop" />
                            {{ $t('navs.userDropdown.accounts') }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item
                        command="openUsers"
                        :divided="
                            !authUser.hasAccessTo('groups.index') &&
                            !!authUser.hasAccessTo('accounts.index')
                        "
                        v-if="!mdLayout && authUser.hasAccessTo('users.index')"
                    >
                        <div class="flex items-center">
                            <icon class="mr-2 text-grey-500" name="regular/multiple-neutral-2" />
                            {{ $t('navs.userDropdown.users') }}
                        </div>
                    </el-dropdown-item>

                    <el-dropdown-item command="online" :divided="!mdLayout">
                        <div class="flex items-center">
                            <icon
                                class="mr-2 text-green-500"
                                name="bold/sign-badge-circle"
                                scale="0.6"
                            />
                            {{ $t('schedule.availability.schedule') }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="offlineToday">
                        <div class="flex items-center">
                            <icon
                                class="mr-2 text-orange-500"
                                name="bold/sign-badge-circle"
                                scale="0.6"
                            />
                            {{ $t('schedule.availability.offlineToday') }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="offlineForever">
                        <div class="flex items-center">
                            <icon
                                class="mr-2 text-red-500"
                                name="bold/sign-badge-circle"
                                scale="0.6"
                            />
                            {{ $t('schedule.availability.offlineForever') }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="openActivixExp" divided>
                        <div class="flex items-center group">
                            <icon class="mr-2 text-grey-500" name="regular/book-open-2" />
                            {{ $t('navs.userDropdown.training') }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="openSupport">
                        <div class="flex items-center group">
                            <icon class="mr-2 text-grey-500" name="regular/question-circle" />
                            {{ $t('navs.userDropdown.help') }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="toggleLocale">
                        <div class="flex items-center group">
                            <icon class="mr-2 text-grey-500" name="regular/earth-1" />
                            {{ $i18n.getContentLanguage() == 'en' ? 'Français' : 'English' }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="openFeedbackModal">
                        <div class="flex items-center">
                            <icon
                                class="mr-2 text-sm"
                                name="regular/messages-bubble-square-information"
                            />
                            {{ $t('navs.userDropdown.suggestions') }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="openChangeLog">
                        <div class="flex items-center">
                            <icon class="mr-2 text-sm" name="regular/megaphone-1" />
                            {{ $t('navs.userDropdown.changelog') }}
                        </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="logout" divided>
                        <div class="flex items-center group">
                            <icon class="mr-2 text-grey-500" name="regular/logout-1" />
                            {{ $t('navs.userDropdown.disconnect') }}
                        </div>
                    </el-dropdown-item>
                </el-dropdown-menu>
            </template>
        </el-dropdown>

        <activix-confirm-modal
            type="info"
            :title="$t('navs.userDropdown.turnOffNotifications')"
            :content="$t('navs.userDropdown.turnOffNotificationsDetails')"
            @approve="activateDoNotDisturb()"
            @deny="deactivateDoNotDisturb()"
            v-model:opened="doNotDisturbModalOpened"
        />

        <support-modal v-model:opened="supportModalOpened" />
    </div>
</template>

<script>
    import { camelCase } from 'lodash-es';
    import { mapActions } from 'pinia';
    import { useUserStore } from '@/store/modules/user/store.js';
    import { useEventBus } from '@/hooks/useEventBus.ts';
    import SupportUrl from '../../mixins/SupportUrl.js';
    import ActivixExpSSO from '../../mixins/ActivixExpSSO.js';

    const { emit } = useEventBus();

    export default {
        mixins: [SupportUrl, ActivixExpSSO],

        data() {
            return {
                doNotDisturbModalOpened: false,
            };
        },

        computed: {
            authUser() {
                return useUserStore().authUser;
            },

            userTooltip() {
                return `
                    <div class="font-semibold">${this.authUser.fullName}</div>
                    ${this.authUser.role}
                `;
            },

            profileUrl() {
                return {
                    name: 'users.update',
                    params: { id: this.authUser.id },
                };
            },

            accountUrl() {
                return {
                    name: 'accounts.update',
                    params: { id: this.authUser.account_id },
                };
            },

            currentStatusColor() {
                switch (this.currentStatus) {
                    case 'offlineToday':
                        return 'text-orange-500';
                    case 'offlineForever':
                        return 'text-red-500';
                    default:
                        return 'text-green-500';
                }
            },

            currentStatus() {
                return camelCase(this.authUser.availability_status);
            },

            showReturnToOldVersion() {
                return process.env.VUE_APP_ENV !== 'production';
            },

            changeLogUrl() {
                if (this.$i18n.getContentLanguage() == 'en') {
                    return process.env.VUE_APP_CHANGELOG_EN;
                }

                return process.env.VUE_APP_CHANGELOG_FR;
            },
        },

        methods: {
            ...mapActions(useUserStore, ['updateParentAuthUser']),

            logout() {
                emit('logout');
            },

            online() {
                this.changeStatus('schedule');
            },

            offlineForever() {
                this.changeStatus('offline_forever');
                this.doNotDisturbModalOpened = true;
            },

            offlineToday() {
                this.changeStatus('offline_today');
                this.doNotDisturbModalOpened = true;
            },

            toggleLocale() {
                const newLocale = this.$i18n.getContentLanguage() == 'en' ? 'fr-CA' : 'en-CA';

                emit('set-locale', newLocale);
            },

            openProfile() {
                this.$router.push(this.profileUrl);
            },

            openAccount() {
                this.$router.push(this.accountUrl);
            },

            openGroups() {
                this.$router.push({ name: 'groups.index' });
            },

            openAccounts() {
                this.$router.push({ name: 'accounts.index' });
            },

            openUsers() {
                this.$router.push({ name: 'users.index' });
            },

            openFeedbackModal() {
                emit('open-feedback-modal');
            },

            activateDoNotDisturb() {
                this.updateParentAuthUser({ do_not_disturb: true }, true);
                this.$api.users.update(this.authUser.id, {
                    do_not_disturb: true,
                });
            },

            deactivateDoNotDisturb() {
                if (this.authUser.do_not_disturb) {
                    this.updateParentAuthUser({ do_not_disturb: false }, true);
                    this.$api.users.update(this.authUser.id, {
                        do_not_disturb: false,
                    });
                    this.$notify.info(this.$t('navs.userDropdown.turningOnNotifications'));
                }
            },

            async changeStatus(status) {
                const oldStatus = this.authUser.availability_status;

                this.updateParentAuthUser({ availability_status: status }, true);
                if (status === 'schedule') {
                    this.deactivateDoNotDisturb();
                }
                try {
                    await this.$api.users.update(this.authUser.id, {
                        availability_status: status,
                    });
                } catch (error) {
                    this.updateParentAuthUser({ availability_status: oldStatus }, true);

                    this.$notify.error(this.$t('users.alerts.updateStatus.error'));

                    throw error;
                }
            },

            openChangeLog() {
                this.$beamer.show();
            },

            onDropdownCommand(command) {
                if (command) {
                    this[command]();
                }
            },
        },
    };
</script>

<style lang="less">
    .availability-status {
        margin-right: 21px;
    }
</style>
